import { useState, useEffect, useCallback } from 'react';
import { Slide, SlideSummary } from '../types';
import { analysisService } from '../services/analysisService';
import { useAuth } from '../context/AuthContext';

export const useAnalysis = (deckId: string) => {
  const { token } = useAuth();
  const [slides, setSlides] = useState<SlideSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSlides = useCallback(async () => {
    if (!token || !deckId) return;

    setLoading(true);
    setError(null);

    try {
      // Request the full list of slides, including those with zero issues
      const data = await analysisService.getSlides(deckId, token, 'all');
      setSlides(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [deckId, token]);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  return { slides, loading, error, refetch: fetchSlides };
};

export const useSlideDetail = (deckId: string, slideNumber: number) => {
  const { token } = useAuth();
  const [slide, setSlide] = useState<Slide | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSlide = useCallback(async () => {
    if (!token || !deckId || !slideNumber) return;

    setLoading(true);
    setError(null);

    try {
      const data = await analysisService.getSlide(deckId, slideNumber, token);
      setSlide(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [deckId, slideNumber, token]);

  useEffect(() => {
    fetchSlide();
  }, [fetchSlide]);

  return { slide, loading, error, refetch: fetchSlide };
};

